import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function toIsoDate(value: Date) {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isValidDate(value: any): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

function startOfDay(value: string | number | Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

export interface ParamConfig<T> {
  defaultValue: T;
  parse: (value: string | null) => T;
  serialize: (value: T) => string | null;
}

export function booleanParam(defaultValue = false): ParamConfig<boolean> {
  return {
    defaultValue,
    parse(value) {
      if (value === "true" || value === "1") return true;
      if (value === "false" || value === "0") return false;
      return defaultValue;
    },
    serialize(value) {
      return value ? "true" : null;
    },
  };
}

export function stringParam(defaultValue: string | null = null): ParamConfig<string | null> {
  return {
    defaultValue,
    parse(value) {
      return value == null || value === "" ? defaultValue : value;
    },
    serialize(value) {
      return value == null || value === "" ? null : String(value);
    },
  };
}

export function upperStringParam(defaultValue: string | null = null): ParamConfig<string | null> {
  return {
    defaultValue,
    parse(value) {
      return value == null || value === "" ? defaultValue : String(value).toUpperCase();
    },
    serialize(value) {
      if (value == null || value === "") return null;
      return String(value).toUpperCase();
    },
  };
}

export function enumParam<T extends string>(values: T[], defaultValue: T | null = null): ParamConfig<T | null> {
  const allowedValues = new Set(values);

  return {
    defaultValue,
    parse(value) {
      return allowedValues.has(value as T) ? (value as T) : defaultValue;
    },
    serialize(value) {
      return value && allowedValues.has(value) ? value : null;
    },
  };
}

export function dateParam(defaultValue: Date | null = null): ParamConfig<Date | null> {
  return {
    defaultValue,
    parse(value) {
      if (!value) return defaultValue;
      const parsed = startOfDay(new Date(`${value}T00:00:00`));
      return isValidDate(parsed) ? parsed : defaultValue;
    },
    serialize(value) {
      if (!isValidDate(value)) return null;
      return toIsoDate(value);
    },
  };
}

export function parseSearchParams(searchParams: URLSearchParams, schema: Record<string, ParamConfig<any>>) {
  return Object.entries(schema).reduce((state, [key, config]) => {
    state[key] = config.parse(searchParams.get(key));
    return state;
  }, {} as Record<string, any>);
}

export function serializeUrlState(state: Record<string, any>, schema: Record<string, ParamConfig<any>>) {
  const searchParams = new URLSearchParams();

  Object.entries(schema).forEach(([key, config]) => {
    const serializedValue = config.serialize(state[key]);
    const serializedDefault = config.serialize(config.defaultValue);

    if (serializedValue != null && serializedValue !== serializedDefault) {
      searchParams.set(key, serializedValue);
    }
  });

  return searchParams;
}

export function resolveUrlStateUpdate(currentSearch: string, schema: Record<string, ParamConfig<any>>, nextState: any) {
  const currentState = parseSearchParams(new URLSearchParams(currentSearch), schema);
  const resolvedState = typeof nextState === "function" ? nextState(currentState) : nextState;
  const mergedState = { ...currentState, ...resolvedState };
  const nextSearchParams = serializeUrlState(mergedState, schema);
  const nextSearch = nextSearchParams.toString();

  return {
    mergedState,
    nextSearch: nextSearch ? `?${nextSearch}` : "",
  };
}

export function useUrlState(schema: Record<string, ParamConfig<any>>) {
  const location = useLocation();
  const navigate = useNavigate();
  const latestSearchRef = useRef(location.search);

  useEffect(() => {
    latestSearchRef.current = location.search;
  }, [location.search]);

  const state = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseSearchParams(searchParams, schema);
  }, [location.search, schema]);

  const setUrlState = useCallback(
    (nextState: any, options: { replace?: boolean } = {}) => {
      const { nextSearch } = resolveUrlStateUpdate(latestSearchRef.current, schema, nextState);
      latestSearchRef.current = nextSearch;

      navigate(
        {
          pathname: location.pathname,
          search: nextSearch,
        },
        { replace: options.replace ?? false }
      );
    },
    [location.pathname, navigate, schema]
  );

  return [state, setUrlState] as const;
}

export interface UseUrlOverlayStateProps {
  idKey: string;
  modeKey: string;
  defaultMode?: string | null;
  state: Record<string, any>;
  setState: (nextState: any, options?: any) => void;
}

export function useUrlOverlayState({ idKey, modeKey, defaultMode = null, state, setState }: UseUrlOverlayStateProps) {
  const selectedId = state[idKey] ?? null;
  const mode = state[modeKey] ?? null;
  const isOpen = Boolean(selectedId);

  const openOverlay = useCallback(
    (id: string, nextMode: string | null = defaultMode, options = {}) => {
      setState(
        {
          [idKey]: id,
          [modeKey]: nextMode,
        },
        options
      );
    },
    [defaultMode, idKey, modeKey, setState]
  );

  const closeOverlay = useCallback(
    (options = {}) => {
      setState(
        {
          [idKey]: null,
          [modeKey]: null,
        },
        options
      );
    },
    [idKey, modeKey, setState]
  );

  return {
    selectedId,
    mode,
    isOpen,
    openOverlay,
    closeOverlay,
  };
}
